<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="space-x-3">
                <span> {{ $t("ticker.menu.select_product") }}</span>
            </div>
        </template>
        <template #sidebarMain>
            <CardPreloader v-if="isLoading" />
            <div class="p-6 space-y-5">
                <!-- step 1  -->
                <template v-if="currentStep === 0">
                    <p class="ct-text">
                        {{ $t("ticker.product.info") }}
                    </p>
                    <template v-if="!showNotATenant">
                        <ButtonPrimaryMd @click="getECData" class="w-full">
                            {{ $t("ticker.product.get_products") }}
                        </ButtonPrimaryMd>
                        <ButtonSecondaryMd @click="addProduct" class="w-full">
                            <AddIcon />
                            <span>{{ $t("ticker.product.add_new_product") }}</span>
                        </ButtonSecondaryMd>
                    </template>

                    <template v-else>
                        <NotificationAlert
                            :subtitle="$t('ticker.product.not_tenant')"
                            :close-button="false"
                        />

                        <CardEmpty
                            class="w-full"
                            :title="$t('ticker.product.add_new_product')"
                            :subtitle="$t('ticker.product.first_create_product')"
                            :icon="emptyIcon"
                            :ctaLabel="$t('ticker.product.create_product')"
                            @add="addProduct"
                        />
                    </template>
                </template>
                <!-- step 2 -->
                <template v-if="currentStep === 1">
                    <ButtonSecondaryMd @click="selectAllProducts" class="w-full">
                        <AddIcon />
                        <span>{{ $t("ticker.product.select_all_products") }}</span>
                    </ButtonSecondaryMd>

                    <p class="ct-text">
                        {{ $t("ticker.product.select_product") }}
                    </p>

                    <ProductCard
                        v-for="item in unselectedProducts"
                        :key="item.id"
                        :item="item"
                        @click="selectProduct(item)"
                    />

                    <CardEmpty
                        v-if="!list.length"
                        class="w-full"
                        :title="$t('ticker.product.add_new_product')"
                        :subtitle="$t('ticker.product.first_create_product')"
                        :icon="emptyIcon"
                        :ctaLabel="$t('ticker.product.create_product')"
                        @add="addProduct"
                    />
                </template>
                <!-- step 3  -->
                <template v-if="currentStep === 2">
                    <p class="ct-text">
                        {{ $t("ticker.product.connected") }}
                    </p>

                    <ProductCard
                        v-for="item in selectedProducts"
                        :key="item.id"
                        :item="item"
                        isSelected
                        @remove="clearConnection(item.id)"
                    />

                    <ButtonSecondaryMd
                        v-if="unselectedProducts.length"
                        class="w-full"
                        @click="addExtraProdct()"
                    >
                        <AddIcon />
                        <span>{{ $t("ticker.product.add_other_product") }} </span>
                    </ButtonSecondaryMd>
                </template>
            </div>
        </template>
    </PageSidebar>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { ButtonPrimaryMd, ButtonSecondaryMd } from "@easy/ui/buttons";
import { NotificationAlert } from "@easy/ui/notifications";
import { CardEmpty, CardPreloader } from "@easy/ui/cards";
import { ShippingBoxIcon, AddIcon } from "@easy/ui/icons";
import ProductCard from "./product/ProductCard.vue";

import { makeLogger } from "@/shared/consola";
import { userService } from "@/shared/user/user.service";
import { userProductsService } from "@/shared/user/user-products.service";
// eslint-disable-next-line no-unused-vars
const logger = makeLogger("ticker sidebar Paywall");

export default {
    components: {
        ButtonPrimaryMd,
        PageSidebar,
        NotificationAlert,
        CardEmpty,
        CardPreloader,
        ProductCard,
        AddIcon,
        ButtonSecondaryMd,
    },
    data() {
        return {
            isLoading: false,
            showNotATenant: false,
            list: [],
            dataReady: false,
            currentStep: 0,
            // complete / current / upcomming
            steps: [
                { name: "Step 1", status: "current" },
                { name: "Step 2", status: "upcomming" },
                { name: "Step 3", status: "upcomming" },
            ],
        };
    },
    computed: {
        ...mapState("ticker", ["ticker"]),
        ...mapGetters(["easyUrls"]),
        emptyIcon() {
            return ShippingBoxIcon;
        },
        unselectedProducts() {
            return this.list.filter(
                (product) => !this.ticker.ecProductIds.find((item) => item.id === product.id)
            );
        },
        selectedProducts() {
            return this.ticker.ecProductIds
                .map((product) => this.list.find((item) => item.id === product.id))
                .filter(Boolean);
        },
        hasAnyProductConnected() {
            return Boolean(this.ticker?.ecProductIds?.length);
        },
    },
    async mounted() {
        this.assureUserReady(async () => {
            if (this.hasAnyProductConnected) {
                const { result = [] } = await userProductsService.getAll();
                this.list = result;
                this.currentStep = 2;
            }
        });
    },
    methods: {
        ...mapActions(["showServerError", "assureUserReady"]),
        ...mapActions("ticker", ["getECProducts", "connectWithECProduct", "connectWithECProducts"]),
        async getECData() {
            this.isLoading = true;
            const notTenantErrors = [
                "ERR_INT_EASY_CART_NOT_A_TENANT",
                "ERR_INT_EASY_CART_USER_NOT_FOUND",
            ];

            const ecData = await this.getECProducts();
            if (ecData?.message && ecData.message === "SUCCESS") {
                this.list = ecData.result?.products;
                this.dataReady = true;
                this.goToStep(1);
            } else if (ecData?.message && notTenantErrors.includes(ecData.message)) {
                logger.trace("ecData tenant error", ecData);
                this.showNotATenant = true;
            } else {
                logger.trace("ecData other error", ecData);
                this.showServerError(this.$t("cart.connection_problem"));
            }
            this.isLoading = false;
        },
        addExtraProdct() {
            if (!this.dataReady) {
                this.getECData();
            } else {
                this.goToStep(1);
            }
        },
        selectProduct(item) {
            this.connectWithECProduct({ id: item.id, add: true });

            this.currentStep = 2;
        },
        selectAllProducts() {
            this.connectWithECProducts({
                ids: this.unselectedProducts.map((x) => x.id),
                add: true,
            });

            this.currentStep = 2;
        },
        setStep(index) {
            this.steps.slice(0, index).forEach((step) => {
                step.status = "complete";
            });
            this.steps[index].status = "current";
            this.steps.slice(index + 1, this.steps.length).forEach((step) => {
                step.status = "upcomming";
            });
        },
        clearConnection(clearId) {
            this.connectWithECProduct({ id: clearId, add: false });

            this.goToStep(2);
        },
        async addProduct() {
            const newProductLink = `${this.easyUrls.cart}creator/products/new`;
            const { magicUrl } = await userService.forgeMagicLink(newProductLink);
            window.location = magicUrl;
        },
        goToStep(step) {
            this.currentStep = step;
        },
    },
};
</script>
